/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import { AssistantIcon } from 'modules/common/components';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import CollapseIcon from 'modules/common/components/collapse-icon';
import { ENVIRONMENT } from 'config';
import { EXPORT_PDF_BUTTON_STYLE } from 'modules/dashboard/components/tab-container/workbook-report/style';
import MuiTabular from 'modules/common/components/mui-tabular';
import GetAppIcon from '@mui/icons-material/GetApp';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { selectVisualFilters } from 'modules/dashboard/selectors';
import { isEmpty } from '../../../../../../../../common/helpers/object';
import { paceActions } from '../../../../slice';
import {
  selectIsPaceDetailed,
  selectPaceChartBreakdown,
  selectPaceCumulativeView,
  selectPaceLagAligned,
  selectPaceLoading,
  selectPaceTabularData,
  selectPaceHotelSegmentTabularFilterList,
  selectRevenueIsDisplayPS,
} from '../../../../selectors';
import TabularFilterHeader from '../filter-header';
import {
  getPaceHotelSegmentFileName,
  getPaceHotelSegmentSchema,
  getPaceHotelSegmentTitle,
  groupPaceDataWithParent,
} from '../../../../functions';
import { getBreakdownName } from '../../../../../trend-tabular-tabs/function';
import usePaceHotelSegmentTabular from '../../../../hooks/use-pace-hotel-segment-tabular';
import { CERULEAN_TABLE_SECONDARY_TEXT_COLOR } from '../../../../constants';
/**
 * Combined pace hotel segment tabular visual report with filters
 */
const CeruleanPaceHotelSegmentTabularWidget = ({ setIsSegmentLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectPaceHotelSegmentTabularFilterList);
  const paceLagAligned = useSelector(selectPaceLagAligned);
  const paceCumulative = useSelector(selectPaceCumulativeView);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayPS);
  const paceTabularData = useSelector(selectPaceTabularData);
  const isPaceDataLoading = useSelector(selectPaceLoading);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const isDetailed = useSelector(selectIsPaceDetailed);
  //
  const tableRef = useRef();
  const { headerData, interpreterAgentData } = usePaceHotelSegmentTabular(
    setIsSegmentLoaded,
    revenueIsDisplay
  );
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const [isExcelExporting, setIsExcelExporting] = useState(false);
  const [isInterpreterExporting, setIsInterpreterExporting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Breakdown values are rendered as child rows of each booking date or lag
  const breakdownName = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
  // The cumulative measure returns BookingDate while the daily one returns selected_booking_date
  const data = groupPaceDataWithParent(
    paceTabularData,
    breakdownName, paceLagAligned ? 'Lag' : 'selected_booking_date'
  );
  const tableSchema = getPaceHotelSegmentSchema(paceCumulative, paceLagAligned);
  const title = getPaceHotelSegmentTitle(paceCumulative, paceLagAligned);
  //
  const handleExpandCollapse = () => {
    if (isExpanded) {
      tableRef?.current?.handleCollapseAll();
    } else {
      tableRef?.current?.handleExpandAll();
    }
    setIsExpanded(!isExpanded);
  };
  //
  return (
    !isEmpty(tabularFilterList) && (
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        className={styles.mainGrid}
        mb={10}
      >
        <Grid className={styles.paceVisualGrid}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ minHeight: 40, px: 2 }}
          >
            <Grid item>
              <Typography>{title}</Typography>
            </Grid>

            <Grid item>
              <Grid container alignItems="center">
                <>
                  <Typography>Export Data</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsExcelExporting(true)}
                    disabled={
                      !revenueIsDisplay || isExcelExporting || isPaceDataLoading || !data?.length
                    }
                    sx={EXPORT_PDF_BUTTON_STYLE}
                    id="pace-js-export-button"
                  >
                    <GetAppIcon />
                  </IconButton>
                </>
                {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                  <Grid item mt={1} mr={4}>
                    <AssistantIcon
                      exportData={interpreterAgentData}
                      visualName={title}
                      isProcessing={isInterpreterExporting}
                      setIsProcessing={setIsInterpreterExporting}
                      disabled={
                        !revenueIsDisplay ||
                        isExcelExporting ||
                        isInterpreterExporting ||
                        isPaceDataLoading
                      }
                      isCerulean
                      getExcelExportData={tableRef?.current?.getExcelData}
                      exportType={exportType}
                      setExportType={setExportType}
                    />
                  </Grid>
                )}

                <CollapseIcon
                  isDisplay={revenueIsDisplay}
                  onClick={() => dispatch(paceActions.setRevenueIsDisplayPS(!revenueIsDisplay))}
                />
              </Grid>
            </Grid>
          </Grid>
          {revenueIsDisplay && (
            <Grid px={2}>
              {!paceTabularData ? (
                <Grid container justifyContent="center" alignItems="center" py={5}>
                  <Typography fontWeight={500}>Loading Data...</Typography>
                </Grid>
              ) : (
                <MuiTabular
                  ref={tableRef}
                  columns={tableSchema}
                  isProcessing={isExcelExporting}
                  isLoadingData={isPaceDataLoading}
                  setIsProcessing={setIsExcelExporting}
                  secondaryTextColor={CERULEAN_TABLE_SECONDARY_TEXT_COLOR}
                  customAggregationRowStyle={{ paddingRight: 1 }}
                  isAggregate
                  headerComponent={
                    <Box
                      width="100%"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="end"
                    >
                      <TabularFilterHeader
                        hotelName={headerData?.hotelName}
                        sections={headerData?.sections}
                        comparisonHotelName={headerData?.comparisonHotelName}
                        chunkSize={1}
                      />
                      <IconButton
                        size="medium"
                        sx={{ mr: 4, mb: 1 }}
                        disabled={isExcelExporting || isPaceDataLoading || !revenueIsDisplay}
                        title={
                          isExpanded
                            ? 'Collapse all expanded rows in the hierarchy'
                            : 'Expand all down one level in the hierarchy'
                        }
                        onClick={handleExpandCollapse}
                      >
                        {isExpanded ? (
                          <UnfoldLessIcon sx={{ fontSize: 20 }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </Box>
                  }
                  data={data}
                  headerFiltersInfo={headerData}
                  fileName={getPaceHotelSegmentFileName(paceCumulative, paceLagAligned)}
                />
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    )
  );
};
//
export default CeruleanPaceHotelSegmentTabularWidget;
